import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _get, _getBlob } from "../../Services/baseApi";
import { errorHandler, successToast } from "../../Utils/toast";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  DASHBOARD_LIVE_URL,
  DASHBOARD_HISTOGRAMS_URL,
  DASHBOARD_HISTOGRAMS_EXPORT_URL,
  DASHBOARD_HISTOGRAMS_EXPORT_DATA_URL,
  PREDICTION_URL,
} from "../../Services/endpoints";
import {
  downloadBlobFile,
  getFilenameFromDisposition,
} from "../../Utils/downloadFile";
import { downloadHistogramPdf } from "../../Utils/dashboardHistogramExportUtils";

const initialState = {
  isLoading: false,
  dashboardLiveV2: null,
  histogramData: null,
  isHistogramLoading: false,
  isHistogramExporting: false,
  predictionData: null,
  predictionLoading: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardLiveThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDashboardLiveThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardLiveV2 = action.payload;
      })
      .addCase(getDashboardLiveThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(getDashboardHistogramsThunk.pending, (state, action) => {
        const silent =
          typeof action.meta.arg === "object" && Boolean(action.meta.arg?.silent);
        if (!silent) state.isHistogramLoading = true;
      })
      .addCase(getDashboardHistogramsThunk.fulfilled, (state, action) => {
        const silent =
          typeof action.meta.arg === "object" && Boolean(action.meta.arg?.silent);
        if (!silent) state.isHistogramLoading = false;
        state.histogramData = action.payload;
      })
      .addCase(getDashboardHistogramsThunk.rejected, (state, action) => {
        const silent =
          typeof action.meta.arg === "object" && Boolean(action.meta.arg?.silent);
        if (!silent) state.isHistogramLoading = false;
      });

    builder
      .addCase(exportDashboardHistogramExcelThunk.pending, (state) => {
        state.isHistogramExporting = true;
      })
      .addCase(exportDashboardHistogramExcelThunk.fulfilled, (state) => {
        state.isHistogramExporting = false;
      })
      .addCase(exportDashboardHistogramExcelThunk.rejected, (state) => {
        state.isHistogramExporting = false;
      });

    builder
      .addCase(exportDashboardHistogramPdfThunk.pending, (state) => {
        state.isHistogramExporting = true;
      })
      .addCase(exportDashboardHistogramPdfThunk.fulfilled, (state) => {
        state.isHistogramExporting = false;
      })
      .addCase(exportDashboardHistogramPdfThunk.rejected, (state) => {
        state.isHistogramExporting = false;
      });

    builder
      .addCase(getPredictionThunk.pending, (state) => {
        state.predictionLoading = true;
      })
      .addCase(getPredictionThunk.fulfilled, (state, action) => {
        state.predictionLoading = false;
        state.predictionData = action.payload;
      })
      .addCase(getPredictionThunk.rejected, (state) => {
        state.predictionLoading = false;
      });
  },
});

export default persistReducer(
  {
    key: "dashboard/persist",
    storage,
    whitelist: [],
  },
  dashboardSlice.reducer
);

export const getDashboardLiveThunk = createAsyncThunk(
  "dashboardSlice/getDashboardLiveThunk",
  async (query = "") => {
    try {
      const response = await _get(
        query ? `${DASHBOARD_LIVE_URL}?${query}` : DASHBOARD_LIVE_URL
      );
      return response?.data?.result ?? response?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);

export const getDashboardHistogramsThunk = createAsyncThunk(
  "dashboardSlice/getDashboardHistogramsThunk",
  async (arg = "") => {
    try {
      const query = typeof arg === "string" ? arg : (arg?.query ?? "");
      const response = await _get(
        query
          ? `${DASHBOARD_HISTOGRAMS_URL}?${query}`
          : DASHBOARD_HISTOGRAMS_URL,
      );
      return response?.data?.result ?? response?.data;
    } catch (error) {
      errorHandler(error);
    }
  },
);

export const exportDashboardHistogramExcelThunk = createAsyncThunk(
  "dashboardSlice/exportDashboardHistogramExcelThunk",
  async (params) => {
    try {
      const response = await _getBlob(DASHBOARD_HISTOGRAMS_EXPORT_URL, params);
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "Waterlogging_Telemetry_Report.xlsx",
      );
      downloadBlobFile(response.data, filename);
      successToast("Telemetry report downloaded (Excel)");
      return true;
    } catch (error) {
      errorHandler(error);
    }
  },
);

export const exportDashboardHistogramPdfThunk = createAsyncThunk(
  "dashboardSlice/exportDashboardHistogramPdfThunk",
  async (params) => {
    try {
      const response = await _get(DASHBOARD_HISTOGRAMS_EXPORT_DATA_URL, params);
      const payload = response?.data?.result ?? response?.data;
      await downloadHistogramPdf(payload);
      successToast("Telemetry report downloaded (PDF)");
      return true;
    } catch (error) {
      errorHandler(error);
    }
  },
);

export const getPredictionThunk = createAsyncThunk(
  "dashboardSlice/getPredictionThunk",
  async (deviceMac) => {
    try {
      const response = await _get(`${PREDICTION_URL}/${deviceMac}/prediction`);
      return response?.data;
    } catch (error) {
      errorHandler(error);
    }
  }
);
